import React, { useState, useEffect, useRef } from "react";
import { marked } from "marked";
import {
  updateStory,
  downloadAttachment,
  getAllSprints,
  getAllUsers,
  deleteStoryAttachment,
  getModulesByStory,
  getAllProjectModules,
  addStoryComment,
  editStoryComment,
  getStoryActivityTimeline,
} from "../../../pages/api/backlog";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getPriorities, getCategories } from "../../../pages/api/masterOptions";

/* ─────────────────────────────────────────────────
   STYLES  — matches screenshot: white modal, gray
   background inputs, no coloured borders, muted labels
───────────────────────────────────────────────── */
const S = {
  overlay: {
    position: "fixed", inset: 0,/* ── fetch comments + activity timeline ── */

    background: "rgba(0,0,0,0.40)",
    zIndex: 1400,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
  },
  modal: {
    background: "#fff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "760px",
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 12px 48px rgba(0,0,0,0.16)",
    overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "22px 24px 16px 24px",
    flexShrink: 0,
  },
  body: {
    overflowY: "auto",
    padding: "0 24px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    flex: 1,
  },
  footer: {
    display: "flex", justifyContent: "flex-end", gap: "10px",
    padding: "14px 24px",
    borderTop: "1px solid #f0f0f0",
    flexShrink: 0,
  },
  /* muted uppercase label — exactly like screenshot */
  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: "600",
    color: "#6b7280",
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    marginBottom: "6px",
  },
  /* gray-bg input with light border */
  input: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    outline: "none",
    background: "#f9fafb",
    color: "#111827",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.15s",
  },
  select: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    outline: "none",
    background: "#f9fafb",
    color: "#111827",
    boxSizing: "border-box",
    fontFamily: "inherit",
    appearance: "none",
    cursor: "pointer",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    paddingRight: "30px",
  },
  /* description box: gray bg, rounded, with toolbar inside */
  descBox: {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#f9fafb",
    overflow: "hidden",
  },
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  errMsg: {
    color: "#e53935",
    fontSize: "12px",
    marginTop: "4px",
  },
  btnPrimary: {
    padding: "8px 28px",
    border: "none",
    borderRadius: "6px",
    background: "#1d4ed8",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  btnSecondary: {
    padding: "8px 24px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
    fontSize: "14px",
  },
};
// Reset default browser margins on markdown-rendered lists so consecutive
// bullet/numbered lines don't show an unexpected gap
if (typeof document !== "undefined" && !document.getElementById("gs-md-reset")) {
  const style = document.createElement("style");
  style.id = "gs-md-reset";
  style.textContent = `
    .gs-md-preview ul, .gs-md-preview ol { margin: 0; padding-left: 20px; }
    .gs-md-preview li { margin: 2px 0; }
    .gs-md-preview p { margin: 0 0 6px 0; }
    .gs-md-preview p:last-child { margin-bottom: 0; }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────────
   MULTI-SELECT DROPDOWN  (no Bootstrap dependency)
───────────────────────────────────────────────── */
const MultiSelectDropdown = ({
  label, placeholder, options, selected,
  onToggle, getKey, getLabel, error, disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length <= 2
        ? selected.map(getLabel).join(", ")
        : `${selected.slice(0, 2).map(getLabel).join(", ")} +${selected.length - 2} more`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && <label style={S.label}>{label}</label>}
      <button
        type="button"
        onClick={() => !disabled && setOpen((p) => !p)}
        style={{
          ...S.select,
          textAlign: "left",
          color: selected.length > 0 ? "#111827" : "#9ca3af",
          background: disabled ? "#f3f4f6" : "#f9fafb",
          cursor: disabled ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {displayText}
      </button>
      {error && <div style={S.errMsg}>{error}</div>}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
          zIndex: 999,
          minWidth: "100%",
          maxHeight: "200px",
          overflowY: "auto",
          padding: "6px 0",
        }}>
          {options.length === 0 && (
            <div style={{ padding: "10px 14px", fontSize: "13px", color: "#9ca3af" }}>
              No options available
            </div>
          )}
          {options.map((opt) => {
            const key = getKey(opt);
            const isChecked = selected.some(
              (s) => String(getKey(s)) === String(key)
            );
            return (
              <label
                key={key}
                style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "7px 14px", cursor: "pointer",
                  fontSize: "13px", color: "#111827",
                  background: "transparent",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggle(opt, isChecked)}
                  style={{ accentColor: "#1d4ed8", cursor: "pointer" }}
                />
                {getLabel(opt)}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Strip existing markdown emphasis markers so re-editing starts clean
const stripMarkdownEmphasis = (text = "") =>
  text.replace(/\*\*(.*?)\*\*/gs, "$1").replace(/\*(.*?)\*/gs, "$1");

/* ─────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────── */
const TaskDetails = ({
  showViewTaskModal,
  setShowViewTaskModal,
  currentStory,
  setCurrentStory,
  onStoryUpdated,
  showAcceptRTS = false,
  onAcceptRTS,
  isAcceptLoading = false,
  readOnly = false,       // ✅ NEW
  showSendRTS = false,    // ✅ NEW
  onSendRTS,              // ✅ NEW
  isSendingRTS = false,   // ✅ NEW
}) => {
  const hasInitialized = useRef(false);
  const cursorPosRef = useRef({ start: 0, end: 0 }); // stores cursor pos on blur
  const commentPostedRef = useRef(false); // tracks if a comment was posted this session
  const [, setSelectedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("write");
  const [activeFormat, setActiveFormat] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [developerUsers, setDeveloperUsers] = useState([]);
  const [testerUsers, setTesterUsers] = useState([]);
  const [titleError, setTitleError] = useState("");
  const [objectiveError, setObjectiveError] = useState("");
  const [pointsError, setPointsError] = useState("");
  const [testPointsError, setTestPointsError] = useState("");
  const [prLinkError, setPrLinkError] = useState("");
  const [moduleError, setModuleError] = useState("");
  const [testPlanLinkError, setTestPlanLinkError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [, setIsAccpetLoading] = useState(false);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [showBulletMenu, setShowBulletMenu] = useState(false);
  const [bulletMenuPos, setBulletMenuPos] = useState({ top: 0, left: 0 });
  const bulletBtnRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentTab, setCommentTab] = useState("write");
  const commentCursorPosRef = useRef({ start: 0, end: 0 });
  const [expandedComments, setExpandedComments] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const myUserId = Number(localStorage.getItem("id"));
  const myRoleId = Number(localStorage.getItem("roleId"));
  const [commentAttachments, setCommentAttachments] = useState([]);
  const commentFileInputRef = useRef(null);
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [mentionUsers, setMentionUsers] = useState([]);
  const token = localStorage.getItem("token");
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    getPriorities(token).then(res => setPriorities(res.data || [])).catch(() => {});
    getCategories(token).then(res => setCategories(res.data || [])).catch(() => {});
  }, [token]);
  const [originalStory, setOriginalStory] = useState(null);

  /* helpers */
  const getComparableData = (story) => ({
    storyTitle: story.storyTitle || "",
    storyDescription: story.storyDescription || "",
    storyPoints: parseFloat(story.storyPoints) || 0,
    testStoryPoints: parseFloat(story.testStoryPoints) || 0,
    status: story.status || "",
    prLink: story.prLink || "",
    testPlanLink: story.testPlanLink || "",
    sprintId: story.sprint?.id || null,
    priority: story.priority || story.storyPriority || "",
    storyType: story.storyType || "",
    moduleIds:
      story.storyModules
        ?.map((sm) => sm?.projectModule?.id)
        .filter(Boolean).sort() || [],
    userIds: story.storyUsers?.map((su) => su.user.id).sort() || [],
    attachmentCount: story.attachments?.length || 0,
  });

  const isBacklogSprintFn = (sprint) =>
    sprint?.sprintName?.toLowerCase() === "backlog" ||
    (sprint?.startDate === null && sprint?.endDate === null);

  const BULLET_OPTIONS = [
    { label: "None", char: "" },
    { label: "Filled circle", char: "●" },
    { label: "Circle", char: "○" },
    { label: "Filled square", char: "■" },
    { label: "Diamond", char: "◆" },
    { label: "Arrow", char: "➤" },
    { label: "Check", char: "✓" },
  ];

  /* ── fetch initial data ── */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [moduleRes, sprintRes, usersRes] = await Promise.all([
          getAllProjectModules(token),
          getAllSprints(token),
          getAllUsers(token),
        ]);
        setModules(
          (moduleRes.data || [])
            .slice()
            // same A-Z order as Admin Reports / Daily Reporting
            .sort((a, b) =>
              (a.projectModuleName || "").localeCompare(b.projectModuleName || "", undefined, { sensitivity: "base" })
            )
        );


        console.log("All users response:", usersRes.data);
        if (usersRes.data && usersRes.data.length > 0) {
          console.log("First user object:", usersRes.data[0]);
          console.log("First user isQaFlag:", usersRes.data[0].isQaFlag);
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const allSprints = sprintRes.data || [];
        const backlogSprint = allSprints.find(isBacklogSprintFn);
        const validSprints = allSprints.filter((s) => {
          if (isBacklogSprintFn(s)) return false;
          const status = (s.sprintStatus || "").toUpperCase();
          if (status !== "PLANNED" && status !== "ACTIVE") return false;
          // ✅ An ACTIVE sprint should always be selectable, even if its
          // end date is missing or has passed (it hasn't been manually stopped yet).
          if (status === "ACTIVE") return true;
          // Only PLANNED sprints need a valid, non-expired end date.
          if (!s.endDate) return false;
          const end = new Date(s.endDate);
          end.setHours(0, 0, 0, 0);
          return end >= today;
        });

        setSprints(validSprints);
        // const filteredUsers = (usersRes.data || []).filter(
        //   (u) =>
        //     u.role?.id === 3 &&
        //     !u.productOwner &&           // ✅ change this line
        //     u.status === "Verified" &&
        //     u.deboardEmp !== true
        // );

        const filteredUsers = (usersRes.data || []).filter(
          (u) =>
            (u.role?.id === 3 || u.role?.id === 6) &&
            !u.productOwner &&
            u.status === "Verified" &&
            u.deboardEmp !== true
        );
        setUsers(filteredUsers);

        setMentionUsers(filteredUsers); // only active, verified users for @ mention

        setDeveloperUsers(filteredUsers.filter((u) => !u.isQaFlag));
        setTesterUsers(filteredUsers.filter((u) => u.isQaFlag === true));


      } catch (e) {
        console.error("Failed to load initial data", e);
      }
    };
    fetchInitialData();
  }, [token]);

  /* ── init / reset on open ── */
  useEffect(() => {
    if (showViewTaskModal && currentStory && !hasInitialized.current) {
      setOriginalStory(getComparableData(currentStory));
      hasInitialized.current = true;
      setModuleError("");
      setTitleError("");
      setObjectiveError("");
      setPointsError("");
      setPrLinkError("");
      setTestPlanLinkError("");
    }
    if (!showViewTaskModal) {
      hasInitialized.current = false;
      commentPostedRef.current = false;
      setCommentAttachments([]);
    }
  }, [showViewTaskModal, currentStory]);

  /* ── fetch activity timeline (includes comments) ── */
  useEffect(() => {
    const fetchActivity = async () => {
      if (!currentStory?.id || !showViewTaskModal) return;
      try {
        const activityRes = await getStoryActivityTimeline(token, currentStory.id);
        const allActivity = activityRes.data || [];
        // comments are activity items where activityType === "COMMENT"
        setComments(allActivity.filter((a) => a.activityType === "COMMENT"));
        setActivityTimeline(allActivity.filter((a) => a.activityType !== "COMMENT"));
      } catch (e) {
        console.error("Failed to load activity", e);
      }
    };
    fetchActivity();
  }, [currentStory?.id, showViewTaskModal, token]);

  /* ── auto-grow comment textarea with content ── */
  useEffect(() => {
    const ta = document.getElementById("td-comment-editor");
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 240) + "px";
  }, [newComment, commentTab]);

  /* ── fetch modules for story ── */
  useEffect(() => {
    const fetchModules = async () => {
      if (!currentStory?.id || modules.length === 0) return;
      try {
        const res = await getModulesByStory(token, currentStory.id);
        const formatted = (res.data || []).map((m) => ({
          projectModule: {
            id: m.projectModule?.id || m.id,
            projectModuleName:
              m.projectModule?.projectModuleName || m.projectModuleName,
          },
        }));
        setCurrentStory((prev) => ({ ...prev, storyModules: formatted }));
      } catch (e) {
        console.error("Failed to fetch story modules:", e);
      }
    };
    fetchModules();
  }, [currentStory?.id, modules, token]);

  /* ── helpers ── */
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    if (days === 1) return "Yesterday";
    return new Date(dateStr).toLocaleDateString();
  };

  const STATUS_DISPLAY_LABELS = {
    TODO: "To Do",
    INPROGRESS: "In Development",
    INTESTING: "In Testing",
    COMPLETED: "Completed",
    BACKLOG: "Backlog",
  };
  const displayStatusLabel = (v) => STATUS_DISPLAY_LABELS[v] || v;

  const groupActivityByDate = (items) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const groups = {};
    (items || []).forEach((item) => {
      const d = new Date(item.createdAt || item.timestamp);
      d.setHours(0, 0, 0, 0);
      let label;
      if (d.getTime() === today.getTime()) label = "TODAY";
      else if (d.getTime() === yesterday.getTime()) label = "YESTERDAY";
      else label = "OLDER";
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    });
    // return in order: TODAY, YESTERDAY, OLDER
    const ordered = [];
    ["TODAY", "YESTERDAY", "OLDER"].forEach((k) => {
      if (groups[k]) ordered.push({ label: k, items: groups[k] });
    });
    return ordered;
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() && commentAttachments.length === 0) return;
    setIsSubmittingComment(true);
    try {
      await addStoryComment(token, currentStory.id, newComment.trim(), commentAttachments);
      commentPostedRef.current = true;
      setNewComment("");
      setCommentAttachments([]);
      const activityRes = await getStoryActivityTimeline(token, currentStory.id);
      const allActivity = activityRes.data || [];
      const newComments = allActivity.filter((a) => a.activityType === "COMMENT");
      setComments(newComments);
      setActivityTimeline(allActivity.filter((a) => a.activityType !== "COMMENT"));

      // ✅ Update commentCount in parent story list so card reflects new count
      setCurrentStory((prev) => ({
        ...prev,
        commentCount: newComments.length,
        comments: newComments,
      }));
      // Update the story in parent stories list directly with new count
      window.dispatchEvent(new CustomEvent("commentCountUpdated", {
        detail: { storyId: currentStory.id, commentCount: newComments.length }
      }));
      window.dispatchEvent(new Event("storyUpdated"));

    } catch (e) {
      toast.error("Failed to add comment");
    } finally {
      setIsSubmittingComment(false);
    }
  };
  const handleSaveEditComment = async (commentId) => {
    const text = newComment.trim();
    if (!text) { toast.error("Comment cannot be empty"); return; }
    setIsSavingEdit(true);
    try {
      await editStoryComment(token, currentStory.id, commentId, text, commentAttachments);
      const activityRes = await getStoryActivityTimeline(token, currentStory.id);
      const allActivity = activityRes.data || [];
      setComments(allActivity.filter((a) => a.activityType === "COMMENT"));
      setActivityTimeline(allActivity.filter((a) => a.activityType !== "COMMENT"));
      setEditingCommentId(null);
      setNewComment("");
      setCommentAttachments([]);
      toast.success("Comment updated");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update comment");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleComposerSubmit = () => {
    if (editingCommentId) {
      handleSaveEditComment(editingCommentId);
    } else {
      handleSubmitComment();
    }
  };

  const handleClose = () => {
    setShowViewTaskModal(false);
    setIsAccpetLoading(false);
  };

  /* ── markdown helpers ── */
  const detectActiveFormat = () => {
    const ta = document.getElementById("td-md-editor");
    if (!ta || !currentStory?.storyDescription) return;
    const before = currentStory.storyDescription.substring(0, ta.selectionStart);
    const line = before.split("\n").pop();
    const active = [];
    if ((before.match(/\*\*/g) || []).length % 2 === 1) active.push("bold");
    if ((before.match(/_/g) || []).length % 2 === 1) active.push("italic");
    if (line.startsWith("# ")) active.push("heading");
    if (line.startsWith("> ")) active.push("quote");
    if (line.startsWith("- ")) active.push("bullet");
    if (/^\d+\.\s/.test(line)) active.push("number");
    setActiveFormat(active);
  };
  const handleMarkdownKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const ta = e.target;
    const value = ta.value;
    const pos = ta.selectionStart;
    const lastLine = value.substring(0, pos).split("\n").pop();
    if (lastLine.startsWith("- ")) {
      e.preventDefault();
      const nt = value.substring(0, pos) + "\n- " + value.substring(pos);
      setCurrentStory((prev) => ({ ...prev, storyDescription: nt }));
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + 3; }, 0);
    } else if (/^\d+\.\s?/.test(lastLine)) {
      e.preventDefault();
      const n = Number(lastLine.match(/^(\d+)\.\s?/)[1]) + 1;
      const nt = value.substring(0, pos) + "\n" + n + ". " + value.substring(pos);
      setCurrentStory((prev) => ({ ...prev, storyDescription: nt }));
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + (n + ". ").length + 1; }, 0);
    }
  };

  const insertAtCursor = (before, after = "") => {
    const ta = document.getElementById("td-md-editor");
    if (!ta) return;
    // If textarea still has focus, use live position; otherwise use saved ref
    const s = (document.activeElement === ta)
      ? ta.selectionStart
      : cursorPosRef.current.start;
    const e2 = (document.activeElement === ta)
      ? ta.selectionEnd
      : cursorPosRef.current.end;
    const text = currentStory?.storyDescription || "";
    const nt =
      text.substring(0, s) +
      before +
      text.substring(s, e2) +
      after +
      text.substring(e2);
    const newCursorPos = s + before.length;
    cursorPosRef.current = { start: newCursorPos, end: newCursorPos };
    setCurrentStory((prev) => ({ ...prev, storyDescription: nt }));
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = newCursorPos;
      ta.selectionEnd = newCursorPos;
    }, 0);
  };

  const insertAtCommentCursor = (before, after = "") => {
    if (commentTab !== "write") {
      setCommentTab("write");
      setTimeout(() => insertAtCommentCursor(before, after), 0);
      return;
    }
    const ta = document.getElementById("td-comment-editor");
    if (!ta) return;
    ta.focus();

    // Always read live selection AND live text straight from the DOM —
    // never from React state, which can be a render behind on fast clicks.
    const s = (document.activeElement === ta) ? ta.selectionStart : commentCursorPosRef.current.start;
    const e2 = (document.activeElement === ta) ? ta.selectionEnd : commentCursorPosRef.current.end;
    const text = ta.value;
      const hadSelection = e2 > s;
    const rawSelected = hadSelection ? text.substring(s, e2) : "";

    // Keep leading/trailing whitespace OUTSIDE the markers — a space just
    // inside ** or * breaks markdown emphasis parsing entirely.
    const leadingWs = rawSelected.match(/^\s*/)[0];
    const trailingWs = rawSelected.match(/\s*$/)[0];
    const core = rawSelected.slice(
      leadingWs.length,
      trailingWs.length ? rawSelected.length - trailingWs.length : undefined
    ) || rawSelected;

    // Toggle off if the core text is already wrapped with these exact markers
    const coreStart = s + leadingWs.length;
    const coreEnd = e2 - trailingWs.length;
    const alreadyWrapped =
      before && after &&
      text.substring(coreStart - before.length, coreStart) === before &&
      text.substring(coreEnd, coreEnd + after.length) === after;

    let nt, selStart, selEnd;
    if (alreadyWrapped) {
      nt = text.substring(0, coreStart - before.length) + core + text.substring(coreEnd + after.length);
      selStart = coreStart - before.length;
      selEnd = selStart + core.length;
    } else {
      nt = text.substring(0, s) + leadingWs + before + core + after + trailingWs + text.substring(e2);
      selStart = s + leadingWs.length + before.length;
      selEnd = hadSelection
        ? s + leadingWs.length + before.length + core.length + after.length
        : selStart + core.length;
    }

    commentCursorPosRef.current = { start: selEnd, end: selEnd };
    setNewComment(nt);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = selStart;
      ta.selectionEnd = selEnd;
    }, 0);
  };
  const insertBulletAtCommentCursor = () => {
    if (commentTab !== "write") {
      setCommentTab("write");
      setTimeout(() => insertBulletAtCommentCursor(), 0);
      return;
    }
    const ta = document.getElementById("td-comment-editor");
    if (!ta) return;
    ta.focus();

    const pos = (document.activeElement === ta) ? ta.selectionStart : commentCursorPosRef.current.start;
    const text = ta.value;

    const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
    const currentLine = text.substring(lineStart, pos);

    // If the current line is empty, just prefix it; otherwise start a fresh bullet line below.
    const insertion = currentLine.trim() === "" ? "- " : "\n- ";
    const newPos = pos + insertion.length;

    const nt = text.substring(0, pos) + insertion + text.substring(pos);
    commentCursorPosRef.current = { start: newPos, end: newPos };
    setNewComment(nt);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = newPos;
      ta.selectionEnd = newPos;
    }, 0);
  };

  const toggleBulletMenu = () => {
    if (bulletBtnRef.current) {
      const rect = bulletBtnRef.current.getBoundingClientRect();
      setBulletMenuPos({ top: rect.top - 8, left: rect.left });
    }
    setShowBulletMenu((p) => !p);
  };

  const handleCommentKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const ta = e.target;
    const value = ta.value;
    const pos = ta.selectionStart;
    const lastLine = value.substring(0, pos).split("\n").pop();

    if (e.shiftKey) return; // allow normal newline with Shift+Enter

    if (lastLine.startsWith("- ")) {
      e.preventDefault();
      const nt = value.substring(0, pos) + "\n- " + value.substring(pos);
      setNewComment(nt);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + 3; }, 0);
      return;
    }
    if (/^\d+\.\s?/.test(lastLine)) {
      e.preventDefault();
      const n = Number(lastLine.match(/^(\d+)\.\s?/)[1]) + 1;
      const nt = value.substring(0, pos) + "\n" + n + ". " + value.substring(pos);
      setNewComment(nt);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = pos + (n + ". ").length + 1; }, 0);
      return;
    }
    e.preventDefault();
    handleSubmitComment();
  };

  /* ── field change handlers ── */
  const handleStoryChange = (e) => {
    const v = e.target.value;
    const t = v.trim();
    setCurrentStory((prev) => ({ ...prev, storyTitle: v }));
    if (!v) { setTitleError(""); return; }
    if (!t) { setTitleError("Title cannot be spaces only"); return; }
    if (/^[0-9]+$/.test(t)) { setTitleError("Title cannot be numbers only"); return; }
    if (/^[^a-zA-Z0-9]+$/.test(t)) { setTitleError("Title cannot contain only special characters"); return; }
    setTitleError("");
  };

  const handleObjectiveChange = (e) => {
    const v = e.target.value;
    const t = v.trim();
    setCurrentStory((prev) => ({ ...prev, storyDescription: v }));
    if (!v) { setObjectiveError(""); return; }
    if (!t) { setObjectiveError("Description cannot be spaces only"); return; }
    if (/^[0-9]+$/.test(t)) { setObjectiveError("Description cannot be numbers only"); return; }
    if (/^[^a-zA-Z0-9]+$/.test(t)) { setObjectiveError("Description cannot contain only special characters"); return; }
    setObjectiveError("");
  };

  const handlePointsChange = (e) => {
    const v = e.target.value;
    const t = v.trim();
    setCurrentStory((prev) => ({ ...prev, storyPoints: v }));
    if (!t) { setPointsError(v !== "" ? "Story Points cannot be spaces only" : ""); return; }
    if (!/^\d+(\.\d+)?$/.test(t)) { setPointsError("Story Points must be a number"); return; }
    setPointsError("");
  };

  const handleTestPointsChange = (e) => {
    const v = e.target.value;
    const t = v.trim();
    setCurrentStory((prev) => ({ ...prev, testStoryPoints: v }));
    if (!t) { setTestPointsError(v !== "" ? "Test Story Points cannot be spaces only" : ""); return; }
    if (!/^\d+(\.\d+)?$/.test(t)) { setTestPointsError("Test Story Points must be a number"); return; }
    setTestPointsError("");
  };

  const handlePrLinkChange = (e) => {
    const v = e.target.value;
    setCurrentStory((prev) => ({ ...prev, prLink: v }));
    if (!v) { setPrLinkError(""); return; }
    if (!v.trim()) { setPrLinkError("PR Link cannot be spaces only"); return; }
    if (/^[^a-zA-Z0-9]*$/.test(v.trim())) { setPrLinkError("PR Link must contain alphanumeric characters"); return; }
    setPrLinkError("");
  };

  const handleTestPlanLinkChange = (e) => {
    const v = e.target.value;
    setCurrentStory((prev) => ({ ...prev, testPlanLink: v }));
    if (!v) { setTestPlanLinkError(""); return; }
    if (!v.trim()) { setTestPlanLinkError("Test Plan Link cannot be spaces only"); return; }
    if (/^[^a-zA-Z0-9]*$/.test(v.trim())) { setTestPlanLinkError("Test Plan Link must contain alphanumeric characters"); return; }
    setTestPlanLinkError("");
  };

  const handleAttachmentUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    setCurrentStory((prev) => ({
      ...prev,
      attachments: [
        ...(prev.attachments || []),
        ...files.map((f) => ({ fileObj: f, fileName: f.name, isNew: true })),
      ],
    }));
    e.target.value = null;
  };

  const handleAttachmentDownload = async (att) => {
    try {
      // ✅ Newly-picked file not yet saved to the server — download straight from the browser file object
      if (att?.isNew && att?.fileObj) {
        const url = window.URL.createObjectURL(att.fileObj);
        const a = document.createElement("a");
        a.href = url;
        a.download = att.fileName || att.fileObj.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        return;
      }

      const fileUrl = att?.attachmentUrl || att?.fileUrl || att?.filePath;
      console.log("fileUrl=", fileUrl);
      console.log("fileUrl as attachmentUrl =", att?.attachmentUrl);
      console.log("fileUrl as fileUrl=", att?.fileUrl);
      console.log("fileUrl as filePath=", att?.filePath);
      if (!fileUrl) return toast.error("Invalid attachment");
      const filename = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);
      const response = await downloadAttachment(filename, token);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att?.attachmentName || filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download attachment");
    }
  };

  const handleAttachmentRemove = (att, index) => {
    setAttachmentToDelete({ att, index });
    setShowDeleteModal(true);
  };

  const confirmDeleteAttachment = async () => {
    const { att, index } = attachmentToDelete;
    setShowDeleteModal(false);
    if (att.isNew) {
      setCurrentStory((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
      setSelectedFiles((prev) => prev.filter((f) => f.name !== att.fileName));
      toast.success("Attachment removed");
      return;
    }
    try {
      await deleteStoryAttachment(att.id, token);
      setCurrentStory((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index),
      }));
      setSelectedFiles([]);
      toast.success("Attachment deleted successfully");
    } catch {
      toast.error("Failed to delete attachment");
    }
  };

  /* ── save / update ── */
  const handleUpdateStory = async () => {
    setTitleError(""); setObjectiveError(""); setPointsError("");
    setPrLinkError(""); setTestPlanLinkError(""); setModuleError("");

    let hasError = false;
    const isBacklogSelected =
      currentStory?.sprint &&
      (currentStory.sprint.sprintName?.toLowerCase() === "backlog" ||
        (currentStory.sprint.startDate === null &&
          currentStory.sprint.endDate === null));

    if (
      isBacklogSelected &&
      (currentStory?.sentForRTS === true || currentStory?.rtsAccepted === true)
    ) {
      toast.error("RTS sent or accepted stories cannot be moved to Backlog");
      return;
    }

    const { storyTitle, storyDescription, storyPoints, prLink, testPlanLink } =
      currentStory || {};

    if (!readOnly) {
      if (!storyTitle || !storyTitle.trim()) {
        setTitleError("Story/Task Title is required"); hasError = true;
      } else if (/^[0-9]+$/.test(storyTitle.trim())) {
        setTitleError("Story/Task Title cannot be numbers only"); hasError = true;
      } else if (/^[^a-zA-Z0-9]+$/.test(storyTitle.trim())) {
        setTitleError("Story/Task Title cannot contain only special characters"); hasError = true;
      }

      if (!storyDescription || !storyDescription.trim()) {
        setObjectiveError("Story Description is required"); hasError = true;
      } else if (/^[0-9]+$/.test(storyDescription.trim())) {
        setObjectiveError("Story Description cannot be numbers only"); hasError = true;
      } else if (/^[^a-zA-Z0-9]+$/.test(storyDescription.trim())) {
        setObjectiveError("Story Description cannot contain only special characters"); hasError = true;
      }
    }

    if (storyPoints !== undefined && storyPoints !== null) {
      const tp = String(storyPoints).trim();
      if (tp === "" && storyPoints !== "") {
        setPointsError("Story Points cannot be spaces only"); hasError = true;
      } else if (tp !== "" && !/^\d+(\.\d+)?$/.test(tp)) {
        setPointsError("Story Points must be a number (decimal allowed)"); hasError = true;
      }
    }

    if (prLink) {
      if (!prLink.trim()) { setPrLinkError("PR Link cannot be spaces only"); hasError = true; }
      else if (/^[^a-zA-Z0-9]*$/.test(prLink.trim())) { setPrLinkError("PR Link must contain alphanumeric characters"); hasError = true; }
    }

    if (testPlanLink) {
      if (!testPlanLink.trim()) { setTestPlanLinkError("Test Plan Link cannot be spaces only"); hasError = true; }
      else if (/^[^a-zA-Z0-9]*$/.test(testPlanLink.trim())) { setTestPlanLinkError("Test Plan Link must contain alphanumeric characters"); hasError = true; }
    }

    // REPLACE WITH:
    if (!readOnly && (!currentStory?.storyModules || currentStory.storyModules.length === 0)) {
      setModuleError("Please select at least one module"); hasError = true;
    }

    if (
      currentStory?.status === "INPROGRESS" || currentStory?.status === "COMPLETED"
    ) {
      if (!currentStory?.sprint?.id || isBacklogSprintFn(currentStory.sprint)) {
        toast.error("Please select a sprint before setting status to In Progress or Completed.");
        hasError = true;
      }
    }

    if (hasError) return;

    if (
      JSON.stringify(getComparableData(currentStory)) ===
      JSON.stringify(originalStory)
    ) {
      if (commentPostedRef.current) {
        handleClose();
        return;
      }
      toast.info("No changes made");
      return;
    }

    setIsSaving(true);
    try {
      const storyPayload = {
        storyTitle: currentStory.storyTitle,
        storyDescription: currentStory.storyDescription,
        storyType: currentStory.storyType || "",
        priority: currentStory.priority || currentStory.storyPriority || "",
        storyPoints: parseFloat(currentStory.storyPoints),
        testStoryPoints: currentStory.testStoryPoints ? parseFloat(currentStory.testStoryPoints) : null,

        status: currentStory.status || "TODO",
        sprintId: currentStory.sprint?.id || null,
        prLink: currentStory.prLink,
        testPlanLink: currentStory.testPlanLink,
        moduleIds: currentStory.storyModules?.map((sm) => sm?.projectModule?.id).filter(Boolean) || [],
        userIds: currentStory.storyUsers?.map((su) => ({
          userId: su.user.id,
          userRole: su.userRole || "DEVELOPER",
        })) || [],
        deleteAttachment: currentStory.attachments.length === 0,
      };
      const formData = new FormData();
      formData.append("story", JSON.stringify(storyPayload));
      const newAtts =
        currentStory.attachments?.filter((a) => a.isNew && a.fileObj) || [];
      newAtts.forEach((a) => formData.append("file", a.fileObj));

      const response = await updateStory(currentStory.id, formData, token);
      toast.success("Story updated successfully!");
      // ✅ Update currentStory with the saved SP values so reopening shows correct data
      setCurrentStory((prev) => ({
        ...prev,
        storyPoints: parseFloat(currentStory.storyPoints) || prev.storyPoints,
        testStoryPoints: parseFloat(currentStory.testStoryPoints) || prev.testStoryPoints,
      }));
      window.dispatchEvent(new Event("storyUpdated"));
      window.dispatchEvent(new Event("sprintStoryUpdated"));
      if (typeof onStoryUpdated === "function") onStoryUpdated(response.data);
      handleClose();
    } catch (e) {
      console.error("Update error:", e);
      toast.error("Failed to update story");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendRTSClick = async () => {
    if (!currentStory?.prLink?.trim() || currentStory.prLink === "null") {
      toast.error("Please add a PR Link before sending for RTS");
      return;
    }
    try {
      setIsSaving(true);
      const storyPayload = {
        storyTitle: currentStory.storyTitle,
        storyDescription: currentStory.storyDescription,
        storyType: currentStory.storyType || "",
        priority: currentStory.priority || currentStory.storyPriority || "",
        storyPoints: parseFloat(currentStory.storyPoints),
        testStoryPoints: currentStory.testStoryPoints ? parseFloat(currentStory.testStoryPoints) : null,
        status: currentStory.status || "TODO",
        sprintId: currentStory.sprint?.id || null,
        prLink: currentStory.prLink,
        testPlanLink: currentStory.testPlanLink,
        moduleIds: currentStory.storyModules?.map((sm) => sm?.projectModule?.id).filter(Boolean) || [],
        userIds: currentStory.storyUsers?.map((su) => ({
          userId: su.user.id,
          userRole: su.userRole || "DEVELOPER",
        })) || [],
        deleteAttachment: currentStory.attachments.length === 0,
      };
      const formData = new FormData();
      formData.append("story", JSON.stringify(storyPayload));
      const newAtts = currentStory.attachments?.filter((a) => a.isNew && a.fileObj) || [];
      newAtts.forEach((a) => formData.append("file", a.fileObj));

      await updateStory(currentStory.id, formData, token);

      if (onSendRTS) await onSendRTS(currentStory);
    } catch (e) {
      console.error("Save before Send RTS failed:", e);
      toast.error("Failed to save PR Link before sending RTS");
    } finally {
      setIsSaving(false);
    }
  };

  if (!showViewTaskModal) return null;

  const story = currentStory || {};
  const storyPriority = story.priority || story.storyPriority || "";
  const storyType = story.storyType || "";

  return (
    <>
      <style>{`
        .gs-md-preview ul, .gs-md-preview ol { margin: 0; padding-left: 20px; }
        .gs-md-preview li { margin: 2px 0; }
        .gs-md-preview p { margin: 0; }
        .gs-md-preview p + p { margin-top: 4px; }
      `}</style>
      {/* ══════════ MODAL ══════════ */}
      <div style={S.overlay} onClick={handleClose}>
        <div style={S.modal} onClick={(e) => e.stopPropagation()}>

          {/* HEADER */}
          <div style={S.header}>
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827" }}>
                Task Details: {story.storyTitle}
              </h2>
              {story.id && (
                <span style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px", display: "block" }}>
                  #{story.id}
                </span>
              )}
            </div>
            <button
              onClick={handleClose}
              style={{
                background: "none", border: "none", fontSize: "22px",
                cursor: "pointer", color: "#9ca3af", lineHeight: 1, padding: "2px",
                marginTop: "2px",
              }}
            >
              ×
            </button>
          </div>

          {/* BODY */}
          <div style={S.body}>

            {/* ── TASK TITLE ── */}
            <div>
              <label style={S.label}>
                TASK TITLE <span style={{ color: "#e53935" }}>*</span>
              </label>
              <input
                style={{ ...S.input, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "default" : "text" }}
                value={story.storyTitle || ""}
                name="storyTitle"
                onChange={readOnly ? undefined : handleStoryChange}
                readOnly={readOnly}
                placeholder="Enter task title"
              />
              {titleError && <div style={S.errMsg}>{titleError}</div>}
            </div>
            {/* ── DESCRIPTION ── */}
            <div>
              <label style={S.label}>
                DESCRIPTION <span style={{ color: "#e53935" }}>*</span>
              </label>

              <div style={S.descBox}>
                {!readOnly && activeTab === "write" ? (
                  <textarea
                    id="td-md-editor"
                    rows={5}
                    name="storyDescription"
                    value={story.storyDescription || ""}
                    onChange={readOnly ? undefined : handleObjectiveChange}
                    readOnly={readOnly}
                    onClick={(e) => { cursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; detectActiveFormat(); }}
                    onKeyUp={(e) => { cursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; detectActiveFormat(); }}
                    onSelect={(e) => { cursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onBlur={(e) => { cursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onKeyDown={handleMarkdownKeyDown}
                    placeholder="Context..."
                    style={{ width: "100%", border: "none", outline: "none", resize: "vertical", fontSize: "14px", fontFamily: "inherit", color: "#374151", lineHeight: "1.6", background: "transparent", boxSizing: "border-box", padding: "12px 14px" }}
                  />
                ) : (
                  <div
                    className="gs-md-preview"
                    style={{
                      minHeight: "120px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      resize: "vertical",
                      fontSize: "14px",
                      color: "#374151",
                      padding: "12px 14px",
                      boxSizing: "border-box",
                    }}
                    dangerouslySetInnerHTML={{ __html: marked(story.storyDescription || "", { breaks: true }) }}
                  />
                )}
                {!readOnly && <div style={{ borderTop: "1px solid #e5e7eb" }} />}

                {!readOnly && (
                  <div style={{ display: "flex", alignItems: "center", gap: "2px", padding: "6px 10px" }}>
                    <div style={{ position: "relative" }}>
                      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setShowMentionDropdown((p) => !p)}
                        style={{ background: showMentionDropdown ? "#e5e7eb" : "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", fontSize: "15px", lineHeight: 1 }}
                        title="Mention a member">@</button>
                      {showMentionDropdown && (
                        <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 999, minWidth: "220px", maxHeight: "200px", overflowY: "auto", padding: "4px 0" }}>
                          <div style={{ padding: "8px 14px 4px 14px", fontSize: "10px", fontWeight: "700", color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" }}>MENTION A MEMBER</div>
                          {mentionUsers.map((u) => (
                            <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { insertAtCursor("@" + (u.email || u.name || "") + " "); setShowMentionDropdown(false); }}
                              style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", border: "none", background: "none", padding: "8px 14px", cursor: "pointer", textAlign: "left", fontSize: "13px", color: "#111827" }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                              onMouseLeave={(e) => (e.currentTarget.style.background = "none")}>
                              <span style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#1d4ed8", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", flexShrink: 0 }}>
                                {(u.firstName?.[0] || u.name?.[0] || u.email?.[0] || "U").toUpperCase()}
                              </span>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email || u.name || "Unknown"}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.getElementById("td-att-upload").click()}
                      style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", lineHeight: 1 }} title="Attach file">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                    </button>
                    <input type="file" id="td-att-upload" style={{ display: "none" }} multiple onChange={handleAttachmentUpload} />

                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCursor("- ")}
                      style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", lineHeight: 1 }} title="Bullet list">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    </button>

                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCursor("**", "**")}
                      style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: activeFormat.includes("bold") ? "#1d4ed8" : "#6b7280", borderRadius: "4px", fontSize: "15px", fontWeight: "700", lineHeight: 1 }}
                      title="Bold">B</button>

                    <div style={{ marginLeft: "auto", display: "flex", gap: "2px" }}>
                      {["write", "preview"].map((t) => (
                        <button key={t} type="button" onClick={() => setActiveTab(t)}
                          style={{ background: activeTab === t ? "#e5e7eb" : "none", border: "none", borderRadius: "4px", padding: "3px 10px", fontSize: "12px", color: activeTab === t ? "#111827" : "#6b7280", fontWeight: activeTab === t ? "600" : "400", cursor: "pointer" }}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {(story.attachments || []).length > 0 && (
                <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {story.attachments.map((att, i) => (
                    <div key={att.id || i} style={{ display: "flex", alignItems: "center", gap: "6px", background: "#f3f4f6", borderRadius: "4px", padding: "3px 10px", fontSize: "12px" }}>
                      <button type="button" onClick={() => handleAttachmentDownload(att)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#1d4ed8", fontSize: "12px", padding: 0 }}>
                        {att.fileName}
                      </button>
                      {!readOnly && (
                        <button type="button" onClick={() => handleAttachmentRemove(att, i)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "14px", lineHeight: 1, padding: 0 }}>
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {objectiveError && <div style={S.errMsg}>{objectiveError}</div>}
            </div>
            {/* ── TYPE ── */}
            <div>
              <label style={S.label}>CATEGORY</label>
              <select
                value={storyType}
                onChange={readOnly ? undefined : (e) => setCurrentStory((prev) => ({ ...prev, storyType: e.target.value }))}
                disabled={readOnly}
                style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.category}>{c.category}</option>
                ))}
              </select>
            </div>

            {/* ── MODULE + ASSIGNED USERS ── */}
            <div style={S.row2}>
              <div>
                <MultiSelectDropdown
                  label={
                    <>
                      SELECT MODULE <span style={{ color: "#e53935" }}>*</span>
                    </>
                  }
                  placeholder="Select Modules"
                  options={
                    readOnly
                      ? []
                      : modules.filter(
                          (m) =>
                            (m.status || "Active") !== "Inactive" ||
                            // keep a module visible if this story already uses it,
                            // so it can still be seen and unticked
                            (story.storyModules || []).some(
                              (sm) => String(sm.projectModule?.id) === String(m.moduleId)
                            )
                        )
                  }
                  selected={story.storyModules || []}
                  getKey={(opt) =>
                    opt?.projectModule?.id !== undefined
                      ? opt.projectModule.id
                      : opt.moduleId
                  }
                  getLabel={(opt) =>
                    opt?.projectModule?.projectModuleName ||
                    opt.projectModuleName ||
                    ""
                  }
                  disabled={readOnly}
                  onToggle={readOnly ? () => { } : (opt, isChecked) => {
                    const moduleId = opt.moduleId ?? opt.projectModule?.id;
                    const moduleName =
                      opt.projectModuleName ?? opt.projectModule?.projectModuleName;
                    setCurrentStory((prev) => {
                      let updated = [...(prev.storyModules || [])];
                      if (!isChecked) {
                        if (
                          !updated.some(
                            (sm) =>
                              String(sm.projectModule?.id) === String(moduleId)
                          )
                        ) {
                          updated.push({
                            projectModule: { id: moduleId, projectModuleName: moduleName },
                          });
                        }
                      } else {
                        updated = updated.filter(
                          (sm) =>
                            String(sm.projectModule?.id) !== String(moduleId)
                        );
                      }
                      return { ...prev, storyModules: updated };
                    });
                    setModuleError("");
                  }}
                  error={moduleError}
                />
              </div>


            </div>

            {/* ── DEVELOPER + TESTER ── */}
            <div style={S.row2}>
              <div>
                <label style={S.label}>DEVELOPER</label>
                <select
                  value={story.storyUsers?.find((su) => su.userRole === "DEVELOPER")?.user?.id ?? ""}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) {
                      setCurrentStory((prev) => ({ ...prev, storyUsers: (prev.storyUsers || []).filter((su) => su.userRole !== "DEVELOPER") }));
                      return;
                    }
                    const selectedUser = users.find((u) => String(u.id) === String(selectedId));
                    if (!selectedUser) return;
                    setCurrentStory((prev) => {
                      const others = (prev.storyUsers || []).filter((su) => su.userRole !== "DEVELOPER");
                      return { ...prev, storyUsers: [...others, { user: selectedUser, userRole: "DEVELOPER" }] };
                    });
                  }}
                  style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
                >

                  <option value="">Select Developer</option>
                  {developerUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>TESTER</label>
                <select
                  value={story.storyUsers?.find((su) => su.userRole === "TESTER")?.user?.id ?? ""}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) {
                      setCurrentStory((prev) => ({ ...prev, storyUsers: (prev.storyUsers || []).filter((su) => su.userRole !== "TESTER") }));
                      return;
                    }
                    console.log("testerUsers:", testerUsers, "selectedId:", selectedId);
                    const selectedUser = testerUsers.find((u) => String(u.id) === String(selectedId));
                    if (!selectedUser) return;
                    setCurrentStory((prev) => {
                      const others = (prev.storyUsers || []).filter((su) => su.userRole !== "TESTER");
                      return { ...prev, storyUsers: [...others, { user: selectedUser, userRole: "TESTER" }] };
                    });
                  }}
                  style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
                >
                  <option value="">Select Tester</option>
                  {testerUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ── DEV SP + TEST SP ── */}
            <div style={S.row2}>
              <div>
                <label style={S.label}>DEV SP</label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.1"
                  value={story.storyPoints || ""}
                  onChange={readOnly ? undefined : handlePointsChange}
                  onKeyPress={readOnly ? undefined : (e) => {
                    if (!/[0-9.]/.test(e.key)) e.preventDefault();
                  }}
                  readOnly={readOnly}
                  placeholder="Enter Dev SP"
                  style={{ ...S.input, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "default" : "text" }}
                />
                {pointsError && <div style={S.errMsg}>{pointsError}</div>}
              </div>
              <div>
                <label style={S.label}>TEST SP</label>
                <input
                  type="text"
                  inputMode="decimal"
                  step="0.1"
                  value={story.testStoryPoints || ""}
                  onChange={readOnly ? undefined : handleTestPointsChange}
                  onKeyPress={readOnly ? undefined : (e) => {
                    if (!/[0-9.]/.test(e.key)) e.preventDefault();
                  }}
                  readOnly={readOnly}
                  placeholder="Enter Test SP"
                  style={{ ...S.input, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "default" : "text" }}
                />
                {testPointsError && <div style={S.errMsg}>{testPointsError}</div>}
              </div>
            </div>

            {/* ── SPRINT + STATUS ── */}
            <div style={S.row2}>
              <div>
                <label style={S.label}>SPRINT <span style={{ color: "#e53935" }}>*</span></label>
                <select
                  value={story.sprint?.id || ""}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    if (!e.target.value) return;
                    const sel = sprints.find((s) => Number(s.id) === Number(e.target.value));
                    setCurrentStory((prev) => ({ ...prev, sprint: sel || null, status: sel ? "TODO" : prev.status }));
                  }}
                  style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
                >
                  <option value="">Backlog</option>
                  {sprints.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.sprintName}{s.endDate ? ` (${s.startDate} to ${s.endDate})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={S.label}>STATUS</label>
                <select
                  value={story.status || "TODO"}
                  disabled={readOnly}
                  onChange={readOnly ? undefined : (e) => setCurrentStory((prev) => ({ ...prev, status: e.target.value }))}
                  style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
                >
                  <option value="TODO">To Do</option>
                  <option value="INPROGRESS">In Development</option>
                  <option value="INTESTING">In Testing</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
            </div>

            {/* ── START DATE + END DATE ── */}
            <div style={S.row2}>
              <div>
                <label style={S.label}>START DATE</label>
                <input
                  type="date"
                  value={story.sprint?.startDate ? story.sprint.startDate.slice(0, 10) : ""}
                  readOnly={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    const endDate = story.sprint?.endDate?.slice(0, 10) || "";
                    if (endDate && e.target.value > endDate) { toast.error("Start Date cannot be after End Date"); return; }
                    setCurrentStory((prev) => ({ ...prev, sprint: { ...prev.sprint, startDate: e.target.value } }));
                  }}
                  style={{ ...S.input, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "text" }}
                />
              </div>
              <div>
                <label style={S.label}>END DATE</label>
                <input
                  type="date"
                  value={story.sprint?.endDate ? story.sprint.endDate.slice(0, 10) : ""}
                  readOnly={readOnly}
                  onChange={readOnly ? undefined : (e) => {
                    const startDate = story.sprint?.startDate?.slice(0, 10) || "";
                    if (startDate && e.target.value < startDate) { toast.error("End Date cannot be before Start Date"); return; }
                    setCurrentStory((prev) => ({ ...prev, sprint: { ...prev.sprint, endDate: e.target.value } }));
                  }}
                  style={{ ...S.input, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "text" }}
                />
              </div>
            </div>

            {/* ── PRIORITY ── */}
            <div style={{ width: "50%", paddingRight: "8px" }}>
              <label style={S.label}>PRIORITY</label>
              <select
                value={storyPriority}
                onChange={readOnly ? undefined : (e) =>
                  setCurrentStory((prev) => ({ ...prev, priority: e.target.value }))
                }
                disabled={readOnly}
                style={{ ...S.select, background: readOnly ? "#f3f4f6" : "#f9fafb", cursor: readOnly ? "not-allowed" : "pointer" }}
              >
                <option value="">Select Priority</option>
                {priorities.map((p) => (
                  <option key={p.id} value={p.priority.toUpperCase()}>{p.priority}</option>                ))}
              </select>
            </div>

            {/* ── COMMENTS + ACTIVITY TIMELINE ── */}
            <div>
              <label style={S.label}>COMMENTS</label>

              {/* ── Add comment input ── */}
              <div style={S.descBox}>
                {commentTab === "write" ? (
                  <textarea
                    id="td-comment-editor"
                    rows={1}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onClick={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onKeyUp={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onSelect={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onBlur={(e) => { commentCursorPosRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; }}
                    onKeyDown={handleCommentKeyDown}
                    style={{ width: "100%", border: "none", outline: "none", resize: "none", overflow: "auto", fontSize: "14px", fontFamily: "inherit", color: "#374151", lineHeight: "1.5", background: "transparent", boxSizing: "border-box", padding: "9px 14px", minHeight: "36px", maxHeight: "240px" }}
                  />
                              ) : (
                  <div
                    style={{ minHeight: "60px", fontSize: "14px", color: "#374151", padding: "12px 14px" }}
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        try {
                          return marked(newComment || "", { breaks: true });
                        } catch (err) {
                          console.error("[Preview] marked() failed:", err);
                          return (newComment || "").replace(/</g, "&lt;");
                        }
                      })(),
                    }}
                  />
                )}

                <div style={{ borderTop: "1px solid #e5e7eb" }} />

                <div style={{ display: "flex", alignItems: "center", gap: "2px", padding: "6px 10px" }}>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => commentFileInputRef.current?.click()}
                    title="Attach file(s) to comment"
                    style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: commentAttachments.length > 0 ? "#1d4ed8" : "#6b7280", borderRadius: "4px", lineHeight: 1 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </button>
                  <input
                    type="file"
                    multiple
                    ref={commentFileInputRef}
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files || []);
                      if (newFiles.length) setCommentAttachments((prev) => [...prev, ...newFiles]);
                      e.target.value = null;
                    }}
                  />

                  <button
                    ref={bulletBtnRef}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={insertBulletAtCommentCursor}
                    style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", lineHeight: 1, display: "flex", alignItems: "center", gap: "2px" }}
                    title="Bullet list"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCommentCursor("**", "**")}
                    style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", fontSize: "15px", fontWeight: "700", lineHeight: 1 }}
                    title="Bold">B</button>

                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertAtCommentCursor("*", "*")}
                    style={{ background: "none", border: "none", padding: "4px 8px", cursor: "pointer", color: "#6b7280", borderRadius: "4px", fontSize: "14px", fontStyle: "italic", lineHeight: 1 }}
                    title="Italic">I</button>

                  <div style={{ marginLeft: "auto", display: "flex", gap: "2px" }}>
                    {["write", "preview"].map((t) => (
<button key={t} type="button" onClick={() => { console.log("[commentTab] clicking:", t); setCommentTab(t); }}                        style={{ background: commentTab === t ? "#e5e7eb" : "none", border: "none", borderRadius: "4px", padding: "3px 10px", fontSize: "12px", color: commentTab === t ? "#111827" : "#6b7280", fontWeight: commentTab === t ? "600" : "400", cursor: "pointer" }}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                {editingCommentId && (
                  <button
                    type="button"
                    onClick={() => { setEditingCommentId(null); setNewComment(""); }}
                    style={{
                      background: "#fff",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      padding: "6px 16px",
                      cursor: "pointer",
                      color: "#374151",
                      fontSize: "12px",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleComposerSubmit}
                  disabled={(editingCommentId ? isSavingEdit : isSubmittingComment) || (!newComment.trim() && commentAttachments.length === 0)}
                  style={{
                    background: (newComment.trim() || commentAttachments.length > 0) ? "#1d4ed8" : "#e5e7eb",
                    border: "none",
                    borderRadius: "6px",
                    padding: "6px 16px",
                    cursor: (newComment.trim() || commentAttachments.length > 0) ? "pointer" : "default",
                    color: (newComment.trim() || commentAttachments.length > 0) ? "#fff" : "#9ca3af",
                    fontSize: "12px",
                    fontWeight: "600",
                    opacity: (editingCommentId ? isSavingEdit : isSubmittingComment) ? 0.6 : 1,
                    transition: "background 0.15s, color 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {editingCommentId
                    ? (isSavingEdit ? "Saving..." : "Save")
                    : (isSubmittingComment ? "Posting..." : "Post")}
                </button>
              </div>

              {commentAttachments.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                  {commentAttachments.map((f, i) => (
                    <div key={f.name + i} style={{
                      display: "flex", alignItems: "center", gap: "6px",
                      background: "#eff6ff", border: "1px solid #bfdbfe",
                      borderRadius: "4px", padding: "3px 10px", fontSize: "12px",
                      color: "#1d4ed8", width: "fit-content",
                    }}>
                      <span style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCommentAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "13px", lineHeight: 1, padding: 0 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}

              {/* ── ACTIVITY TIMELINE (comments + activity merged) ── */}
              {(comments.length > 0 || activityTimeline.length > 0) && (
                <div style={{ marginTop: "20px" }}>

                  {/* Header */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: "12px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                      <span style={{
                        fontSize: "11px", fontWeight: "700", color: "#6b7280",
                        letterSpacing: "0.07em", textTransform: "uppercase",
                      }}>
                        ACTIVITY TIMELINE
                      </span>
                    </div>
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {comments.length + activityTimeline.length} update{(comments.length + activityTimeline.length) !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Grouped by date — comments merged chronologically with activity */}
                  {groupActivityByDate(
                    [...comments, ...activityTimeline].sort(
                      (a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp)
                    )
                  ).map((group) => (
                    <div key={group.label} style={{ marginBottom: "16px" }}>
                      {/* Date label */}
                      <div style={{
                        fontSize: "10px", fontWeight: "700", color: "#9ca3af",
                        letterSpacing: "0.08em", textTransform: "uppercase",
                        marginBottom: "8px", paddingBottom: "4px",
                        borderBottom: "1px solid #f3f4f6",
                      }}>
                        {group.label}
                      </div>

                      {/* Items */}
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {group.items.map((item, idx) => {
                          const firstName = item.user?.firstName || "";
                          const lastName = item.user?.lastName || "";
                          const fullName = (firstName + " " + lastName).trim()
                            || item.user?.name
                            || item.user?.email
                            || "Unknown";
                          const initials = (firstName?.[0] || item.user?.name?.[0] || "U").toUpperCase();
                          const isLast = idx === group.items.length - 1;

                          return (
                            <div key={item.id || idx} style={{ display: "flex", gap: "10px" }}>
                              {/* Avatar + vertical line */}
                              <div style={{
                                display: "flex", flexDirection: "column",
                                alignItems: "center", flexShrink: 0, width: "34px",
                              }}>
                                <div style={{
                                  width: "34px", height: "34px", borderRadius: "50%",
                                  background: "#e0e7ff", color: "#3730a3",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: "11px", fontWeight: "700", zIndex: 1,
                                }}>
                                  {initials}
                                </div>
                                {!isLast && (
                                  <div style={{
                                    width: "1px", flex: 1,
                                    background: "#e5e7eb", minHeight: "16px",
                                  }} />
                                )}
                              </div>

                              {/* Content */}
                              <div style={{
                                paddingBottom: isLast ? "0" : "14px",
                                paddingTop: "6px", flex: 1,
                              }}>
                                {item.activityType === "COMMENT" ? (
                                  <div style={{
                                    fontSize: "13px", color: "#374151", lineHeight: "1.5",
                                    background: "#f3f4f6", borderRadius: "8px",
                                    padding: "8px 12px",
                                  }}>
                                    <span style={{ fontWeight: "600", color: "#111827" }}>{fullName}</span>
                                    {" "}commented:
                                    {(() => {
                                      const rawComment = item.comment || item.text || item.content || "";
                                      const isExpanded = !!expandedComments[item.id];
                                      const isLong = rawComment.length > 220 || rawComment.split("\n").length > 3;
                                      const isMine = item.user?.id === myUserId;
                                      const isEditingThis = editingCommentId === item.id;

                                      if (isEditingThis) {
                                        return (
                                          <div style={{
                                            marginTop: "4px", fontSize: "12px", color: "#1d4ed8",
                                            fontStyle: "italic", display: "flex", alignItems: "center", gap: "8px",
                                          }}>
                                            Editing this comment in the box above
                                            <button
                                              type="button"
                                              onClick={() => { setEditingCommentId(null); setNewComment(""); }}
                                              style={{
                                                background: "none", border: "1px solid #d1d5db", borderRadius: "4px",
                                                padding: "2px 8px", color: "#374151", fontSize: "11px", cursor: "pointer",
                                              }}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        );
                                      }

                                      return (
                                        <>
                                          <div
                                            className="gs-md-preview"
                                            style={{
                                              marginTop: "2px",
                                              ...(!isExpanded && isLong
                                                ? {
                                                  display: "-webkit-box",
                                                  WebkitLineClamp: 3,
                                                  WebkitBoxOrient: "vertical",
                                                  overflow: "hidden",
                                                }
                                                : {}),
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: marked(rawComment, { breaks: true }),
                                            }}
                                          />
                                          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px" }}>
                                            {isLong && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setExpandedComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                                                }
                                                style={{
                                                  background: "none", border: "none", padding: 0,
                                                  cursor: "pointer", color: "#1d4ed8", fontSize: "12px", fontWeight: "600",
                                                }}
                                              >
                                                {isExpanded ? "Show less" : "Show more"}
                                              </button>
                                            )}
                                            {isMine && !String(item.id).startsWith("optimistic-") && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setEditingCommentId(item.id);
                                                  setNewComment(rawComment);
                                                  setCommentTab("write");
                                                  setTimeout(() => {
                                                    const ta = document.getElementById("td-comment-editor");
                                                    if (ta) {
                                                      ta.focus();
                                                      ta.selectionStart = ta.selectionEnd = ta.value.length;
                                                      commentCursorPosRef.current = { start: ta.value.length, end: ta.value.length };
                                                      ta.scrollIntoView({ behavior: "smooth", block: "center" });
                                                    }
                                                  }, 0);
                                                }}
                                                style={{
                                                  background: "none", border: "none", padding: 0,
                                                  cursor: "pointer", color: "#1d4ed8", fontSize: "12px", fontWeight: "600",
                                                  marginLeft: "auto",
                                                }}
                                              >
                                                Edit
                                              </button>
                                            )}
                                          </div>
                                        </>
                                      );
                                    })()}
                                    {(item.attachments || []).length > 0 && (
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                                        {item.attachments.map((att) => (
                                          <div
                                            key={att.id}
                                            onClick={() => handleAttachmentDownload(att)}
                                            style={{
                                              display: "inline-flex", alignItems: "center", gap: "5px",
                                              background: "#fff", border: "1px solid #d1d5db",
                                              borderRadius: "4px", padding: "3px 8px", fontSize: "12px",
                                              color: "#1d4ed8", cursor: "pointer",
                                            }}
                                          >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                            </svg>
                                            {att.fileName}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.5" }}>
                                    <span style={{ fontWeight: "600", color: "#111827" }}>{fullName}</span>
                                    {" "}{item.action || "updated"}
                                    {/* FROM → TO badges */}
                                    {item.fromValue && item.toValue && (
                                      <span style={{ marginLeft: "6px" }}>
                                        <span style={{
                                          display: "inline-block", padding: "1px 8px",
                                          background: "#f3f4f6", borderRadius: "4px",
                                          fontSize: "12px", color: "#374151", marginRight: "4px",
                                        }}>
                                          {displayStatusLabel(item.fromValue)}
                                        </span>
                                        <span style={{
                                          color: "#9ca3af", fontSize: "12px", marginRight: "4px",
                                        }}>→</span>
                                        <span style={{
                                          display: "inline-block", padding: "1px 8px",
                                          background: "#dcfce7", borderRadius: "4px",
                                          fontSize: "12px", color: "#16a34a",
                                        }}>
                                          {displayStatusLabel(item.toValue)}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Timestamp */}
                                <div style={{
                                  display: "flex", alignItems: "center",
                                  gap: "4px", marginTop: "3px",
                                }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                                    stroke="#9ca3af" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                                    {item.createdAt
                                      ? (group.label === "OLDER"
                                        ? new Date(item.createdAt).toLocaleDateString([], {
                                          day: "2-digit", month: "short", year: "numeric",
                                        }) + " " + new Date(item.createdAt).toLocaleTimeString([], {
                                          hour: "2-digit", minute: "2-digit",
                                        })
                                        : new Date(item.createdAt).toLocaleTimeString([], {
                                          hour: "2-digit", minute: "2-digit",
                                        }))
                                      : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── PR LINK + TEST PLAN LINK ── */}
            <div style={S.row2}>
              <div>
                <label style={S.label}>PR LINK</label>
                <textarea
                  rows={3}
                  placeholder="Enter PR Link"
                  value={story.prLink && story.prLink !== "null" ? story.prLink : ""}
                  onChange={handlePrLinkChange}
                  readOnly={false}
                  style={{ ...S.input, resize: "vertical", whiteSpace: "pre-wrap", background: "#f9fafb", cursor: "text" }}
                />
                {prLinkError && <div style={S.errMsg}>{prLinkError}</div>}
              </div>
              <div>
                <label style={S.label}>TEST PLAN LINK</label>
                <textarea
                  rows={3}
                  placeholder="Enter Test Plan Link"
                  value={story.testPlanLink && story.testPlanLink !== "null" ? story.testPlanLink : ""}
                  onChange={handleTestPlanLinkChange}
                  readOnly={false}
                  style={{ ...S.input, resize: "vertical", whiteSpace: "pre-wrap", background: "#f9fafb", cursor: "text" }}
                />
                {testPlanLinkError && <div style={S.errMsg}>{testPlanLinkError}</div>}
              </div>
            </div>

            {/* ── CREATED BY USER (read-only) ── */}
            {story.createdByUser && (
              <div style={S.row2}>
                <div>
                  <label style={S.label}>USER</label>
                  <input
                    type="text"
                    readOnly
                    value={story.createdByUser.name || ""}
                    style={{ ...S.input, background: "#f3f4f6", cursor: "default" }}
                  />
                </div>
                <div>
                  <label style={S.label}>USER MOBILE</label>
                  <input
                    type="text"
                    readOnly
                    value={story.createdByUser.mobile || ""}
                    style={{ ...S.input, background: "#f3f4f6", cursor: "default" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div style={S.footer}>
            {showAcceptRTS && (
              <button
                type="button"
                disabled={!story.sentForRTS || story.rtsAccepted || isAcceptLoading}
                onClick={() => onAcceptRTS(story)}
                style={{
                  ...S.btnSecondary,
                  opacity: !story.sentForRTS || story.rtsAccepted || isAcceptLoading ? 0.5 : 1,
                  cursor: !story.sentForRTS || story.rtsAccepted || isAcceptLoading ? "not-allowed" : "pointer",
                }}
              >
                {isAcceptLoading ? "Processing..." : "Accept RTS"}
              </button>
            )}

            {/* ✅ Send RTS — only for employees on their own tasks */}
            {showSendRTS && (
              <button
                type="button"
                disabled={isSendingRTS || story.sentForRTS || story.rtsAccepted || story.status !== "COMPLETED" || !story.prLink?.trim() || story.prLink === "null"}
                onClick={handleSendRTSClick}
                title={
                  story.status !== "COMPLETED"
                    ? "Task must be Completed before sending for RTS"
                    : !story.prLink?.trim() || story.prLink === "null"
                      ? "Please add a PR Link before sending for RTS"
                      : ""
                }
                style={{
                  ...S.btnSecondary,
                  opacity: isSendingRTS || story.sentForRTS || story.rtsAccepted || story.status !== "COMPLETED" ? 0.5 : 1,
                  cursor: isSendingRTS || story.sentForRTS || story.rtsAccepted || story.status !== "COMPLETED" ? "not-allowed" : "pointer",
                }}
              >
                {isSendingRTS ? "Sending..." : story.sentForRTS ? "RTS Sent" : "Send RTS"}
              </button>
            )}

            <button type="button" onClick={handleClose} style={S.btnSecondary}>
              Close
            </button>

            {/* ✅ Hide Update button when readOnly */}
            <button
              type="button"
              onClick={handleUpdateStory}
              disabled={isSaving}
              style={{ ...S.btnPrimary, opacity: isSaving ? 0.7 : 1 }}
            >
              {isSaving ? "Updating..." : "Update"}
            </button>
          </div>
        </div>
      </div>

      {/* ══════════ DELETE ATTACHMENT CONFIRM ══════════ */}
      {showDeleteModal && (
        <div
          style={{ ...S.overlay, zIndex: 1500 }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "10px",
              width: "100%", maxWidth: "380px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "16px 20px", borderBottom: "1px solid #f0f0f0",
            }}>
              <span style={{ fontWeight: "700", fontSize: "15px", color: "#111827" }}>
                Confirm Delete
              </span>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  background: "none", border: "none", fontSize: "22px",
                  cursor: "pointer", color: "#9ca3af", lineHeight: 1, padding: 0,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "20px", fontSize: "14px", color: "#374151" }}>
              Are you sure you want to delete this attachment?
            </div>
            <div style={{
              display: "flex", justifyContent: "flex-end", gap: "10px",
              padding: "14px 20px", borderTop: "1px solid #f0f0f0",
            }}>
              <button onClick={() => setShowDeleteModal(false)} style={S.btnSecondary}>
                Cancel
              </button>
              <button onClick={confirmDeleteAttachment} style={S.btnPrimary}>
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskDetails;
